import { config } from "@/config/env";
import { authStore } from "@/auth/store";
import { recordDebugEvent, isDebugEnabled } from "@/lib/debug";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly correlationId?: string,
    public readonly details?: unknown,
    public readonly url?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface ErrorBody {
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
    correlationId?: string;
    requestId?: string;
  };
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  /**
   * If `true` (the default), cookie credentials and CSRF state are included.
   * Set to `false` for explicitly anonymous calls (e.g. /healthz).
   */
  authenticated?: boolean;
  /**
   * Manually provide an override token (used in tests).
   */
  token?: string | null;
  correlationId?: string;
  signal?: AbortSignal;
}

function newCorrelationId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = `${config.apiBaseUrl}${path.startsWith("/") ? path : `/${path}`}`;
  const correlationId = options.correlationId ?? newCorrelationId();
  const headers: Record<string, string> = {
    "x-correlation-id": correlationId,
    accept: "application/json",
  };
  if (options.body !== undefined) {
    headers["content-type"] = "application/json";
  }

  const authenticated = options.authenticated !== false;
  const token = options.token ?? null;
  if (options.token !== null && token) {
    headers["authorization"] = `Bearer ${token}`;
  }
  const csrfToken = authStore.csrfToken();
  const method = options.method ?? "GET";
  if (
    authenticated &&
    csrfToken &&
    ["POST", "PATCH", "PUT", "DELETE"].includes(method)
  ) {
    headers["x-csrf-token"] = csrfToken;
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: "include",
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  };
  if (options.signal) init.signal = options.signal;

  const startTime = performance.now();
  if (isDebugEnabled()) {
    recordDebugEvent("api", "request start", {
      method,
      path,
      correlationId,
      csrf: Boolean(csrfToken),
    });
  }

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    const duration = (performance.now() - startTime).toFixed(0);
    if (isDebugEnabled()) {
      recordDebugEvent("api-error", "network error", {
        method,
        path,
        correlationId,
        error: message,
        duration,
      });
    }
    throw new ApiError(
      0,
      "NetworkError",
      `Network request failed for ${method} ${url}: ${message}`,
      correlationId,
      undefined,
      url,
    );
  }

  const duration = (performance.now() - startTime).toFixed(0);

  if (!res.ok) {
    let errBody: ErrorBody | undefined;
    try {
      errBody = (await res.json()) as ErrorBody;
    } catch {
      // ignore parse error
    }
    if (isDebugEnabled()) {
      recordDebugEvent("api-error", "http error", {
        method,
        path,
        status: res.status,
        code: errBody?.error?.code ?? `HTTP_${res.status}`,
        correlationId: errBody?.error?.correlationId ?? correlationId,
        duration,
      });
    }
    throw new ApiError(
      res.status,
      errBody?.error?.code ?? `HTTP_${res.status}`,
      errBody?.error?.message ?? res.statusText,
      errBody?.error?.correlationId ?? correlationId,
      errBody?.error?.details,
      url,
    );
  }

  if (isDebugEnabled()) {
    recordDebugEvent("api", "request success", {
      method,
      path,
      status: res.status,
      correlationId,
      duration,
    });
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const raw = (await res.json()) as
    | { data: T; meta?: unknown }
    | T
    | undefined;

  // The backend wraps every 2xx response in `{ data, meta }`; unwrap so that
  // call sites can treat the resolved value as the underlying entity. We
  // tolerate bare-body responses too in case a future endpoint opts out.
  if (
    raw !== null &&
    typeof raw === "object" &&
    "data" in (raw as Record<string, unknown>)
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}
